import React from 'react';

import styled from 'styled-components';

import { Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { useTranslation } from '../i18n';

const TooltipContent = styled.div`
  padding-bottom: 4px;
  border-bottom: 1px solid ${theme.colors.charcoal700};
`;

const LearnMoreLink = styled(LinkButton)`
  align-self: flex-start;
  padding-top: 4px;
  font-size: ${theme.typography.heading5.fontSize};
  font-weight: ${theme.typography.heading5.fontWeight};
  line-height: ${theme.typography.heading5.lineHeight};
  color: ${theme.colors.adskBlue100};

  &:hover {
    color: ${theme.colors.adskBlue100};
  }
`;

/**
 * Tooltip wrapper that appends an interactive "Learn more" link below the tooltip content.
 * Uses `triggerTarget="tooltip"` so the tooltip stays open while the user hovers over it.
 *
 * @param {object} props
 * @param {React.ReactNode} props.content - The main tooltip text or node.
 * @param {React.ReactNode} props.children - The trigger element that activates the tooltip.
 * @param {() => void} props.onClickLearnMore - Callback fired when the "Learn more" link is clicked.
 * @param {Function} [props.getTargetProps] - Optional getter for additional props on the Tooltip target wrapper.
 */
const TooltipWithLearnMore = ({
  content,
  children,
  onClickLearnMore,
  getTargetProps,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      variant="dark"
      triggerTarget={Tooltip.TRIGGER_TARGETS.TOOLTIP}
      getTargetProps={getTargetProps}
      content={
        <Flex flexDirection="column">
          <TooltipContent>{content}</TooltipContent>
          <LearnMoreLink onClick={onClickLearnMore}>
            {t('LEARN_MORE')}
          </LearnMoreLink>
        </Flex>
      }
    >
      {children}
    </Tooltip>
  );
};

export default TooltipWithLearnMore;
